import type * as React from "react";
import { useState } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, CirclePlus, Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";
import DatePicker from "@/components/ui/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FilePreviewModal } from "@/components/FilePreviewModal";
// Services and Utils
import { postFixAsset } from "@/services/finance";

// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

// ------------------ Types ------------------

interface FixAssetRow {
  name: string;
  count: string | number;
  unit: string;
  receivedDate: string;
  totalAge: string;
  originalValue: string | number;
  depreciationValue: string | number;
  remainingValue: string | number;
  disposalValue: string | number;
  disposalDate: string;
}

interface CreateFixAssetRequest {
  companyId: string;
  yearly: number;
  docFile: string;
  fixAssetDetails: Array<{
    name: string;
    count: number;
    unit: string;
    receivedDate?: string;
    totalAge: string;
    originalValue: number;
    depreciationValue: number;
    remainingValue: number;
    disposalValue: number;
    disposalDate?: string;
  }>;
}

// ------------------ Helper Functions ------------------

/**
 * Format the user-visible string with commas.
 * If wrapped in parentheses => negative => re-wrap after inserting commas.
 */
function formatNumberWithCommas(value: string | number) {
  if (!value && value !== 0) return "";

  const stringValue = String(value || "");
  const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
  const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

  // Handle the case when user is typing a decimal point
  if (numericValue.endsWith(".")) {
    return isNegative ? `(${numericValue})` : numericValue;
  }

  // Split number into integer and decimal parts
  const [integerPart, decimalPart] = numericValue.split(".");

  // Format integer part with commas
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Handle decimal part - limit to 2 decimal places
  const formattedDecimal = decimalPart ? decimalPart.slice(0, 2) : undefined;

  // Combine with decimal part if it exists
  const formattedValue =
    formattedDecimal !== undefined
      ? `${formattedInteger}.${formattedDecimal}`
      : formattedInteger;

  return isNegative ? `(${formattedValue})` : formattedValue;
}

function parseParenthesisNumber(strValue: string | number) {
  if (!strValue) return 0;
  const stringValue = String(strValue).replace(/,/g, ""); // remove commas

  const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
  const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

  const parsed = Number(numericValue) || 0;
  return isNegative ? -parsed : parsed;
}

function handleNumericChange({
  e,
  index,
  fieldName,
  data,
  setData,
}: {
  e: React.ChangeEvent<HTMLInputElement>;
  index: number;
  fieldName:
    | "count"
    | "originalValue"
    | "depreciationValue"
    | "remainingValue"
    | "disposalValue";
  data: FixAssetRow[];
  setData: React.Dispatch<React.SetStateAction<FixAssetRow[]>>;
}) {
  let value = e.target.value;

  // Allow empty input
  if (!value) {
    const newData = [...data];
    newData[index][fieldName] = "";
    setData(newData);
    return;
  }

  // Remove existing commas
  value = value.replace(/,/g, "");

  // Allow numbers, decimal point, and parentheses
  if (!/^[(]?\d*\.?\d*[)]?$/.test(value) && !/^[(]$/.test(value)) {
    return;
  }

  // Handle single opening parenthesis
  if (value === "(") {
    const newData = [...data];
    newData[index][fieldName] = value;
    setData(newData);
    return;
  }

  const newData = [...data];
  newData[index][fieldName] = value;
  setData(newData);
}

function computeSumForField(data: FixAssetRow[], fieldName: keyof FixAssetRow) {
  const sum = data.reduce((acc, row) => {
    return acc + parseParenthesisNumber(row[fieldName]);
  }, 0);
  return sum.toLocaleString();
}

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/fix-assets/add"
)({
  component: AddFixAssets,
});

function AddFixAssets() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = localStorage.getItem("companyId");
  const [year, setYear] = useState<number | undefined>(() => {
    const storedYear = localStorage.getItem("year");
    return storedYear ? Number(storedYear) : undefined;
  });
  const [docFile, setDocFile] = useState("");

  // ------------- State for Fix Assets -------------
  const [fixAssets, setFixAssets] = useState<FixAssetRow[]>([
    // You can initialize with an empty array or a default row:

    {
      name: "ອາຄານ",
      count: 0,
      unit: "",
      receivedDate: "",
      totalAge: "",
      originalValue: 0,
      depreciationValue: 0,
      remainingValue: 0,
      disposalValue: "",
      disposalDate: "",
    },
    {
      name: "ພາຫະນະ",
      count: 0,
      unit: "",
      receivedDate: "",
      totalAge: "",
      originalValue: 0,
      depreciationValue: 0,
      remainingValue: 0,
      disposalValue: "",
      disposalDate: "",
    },
    {
      name: "ອຸປະກອນທຳທຸລະກິດ",
      count: 0,
      unit: "",
      receivedDate: "",
      totalAge: "",
      originalValue: 0,
      depreciationValue: 0,
      remainingValue: 0,
      disposalValue: "",
      disposalDate: "",
    },
    {
      name: "ເຄື່ອງມືຮັບໃຊ້ການຜະລິດ",
      count: 0,
      unit: "",
      receivedDate: "",
      totalAge: "",
      originalValue: 0,
      depreciationValue: 0,
      remainingValue: 0,
      disposalValue: "",
      disposalDate: "",
    },
  ]);

  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.FIX_ASSET_REPORT);

  // ------------------- React Query Mutation -------------------
  const mutation = useMutation({
    mutationFn: (data: CreateFixAssetRequest) => postFixAsset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["fix-assets", companyId, year],
      });

      toast({
        title: "ສຳເລັດ",
        description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
        variant: "success",
      });

      localStorage.setItem("year", JSON.stringify(year));
      navigate({ to: "/report/finance/company/fix-assets" });
    },
    onError: (error: Error) => {
      console.log(error, "Error");
      toast({
        title: "ບໍ່ສາມາດບັນທຶກຂໍ້ມູນໄດ້",
        description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  // ------------------ Handlers ------------------
  /**
   * Add a new row for Fix Asset with default values.
   */
  const addNewFixAssetRow = () => {
    setFixAssets((prev) => [
      ...prev,
      {
        name: "",
        count: 0,
        unit: "",
        receivedDate: "",
        totalAge: "",
        originalValue: 0,
        depreciationValue: 0,
        remainingValue: 0,
        disposalValue: "",
        disposalDate: "",
      },
    ]);
  };

  /**
   * Remove row from fixAssets state by index.
   */
  const removeFixAssetRow = (indexToRemove: number) => {
    setFixAssets((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  /**
   * Handle form submission.
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId || !year) {
      toast({
        title: "ຂໍ້ມູນບໍ່ຄົບຖ້ວນ",
        description: "ກະລຸນາປ້ອນປີ",
        variant: "destructive",
      });
      return;
    }

    if (!docFile) {
      toast({
        title: "ບັນທຶກລົ້ມເຫຼວ",
        description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
        variant: "destructive",
      });
      return;
    }

    // Convert numeric fields from strings to numbers as needed
    const finalFixAssets = fixAssets.map((row) => ({
      name: row.name,
      count: parseParenthesisNumber(row.count || 0),
      unit: row.unit,
      receivedDate: row.receivedDate || undefined,
      totalAge: row.totalAge,
      originalValue: parseParenthesisNumber(row.originalValue),
      depreciationValue: parseParenthesisNumber(row.depreciationValue),
      remainingValue:
        parseParenthesisNumber(row.originalValue) -
        parseParenthesisNumber(row.depreciationValue),
      disposalValue: parseParenthesisNumber(row.disposalValue),
      disposalDate: row.disposalDate || undefined,
    }));

    const data: CreateFixAssetRequest = {
      companyId,
      yearly: year,
      docFile,
      fixAssetDetails: finalFixAssets,
    };

    console.log(data, "Data");

    mutation.mutate(data);
  };

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between">
            <div>
              <CardTitle>ເພີ່ມຂໍ້ມູນຊັບສິນຄົງທີ່</CardTitle>
              <CardDescription>ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* ------------------- Year Input ------------------- */}

            <div className="flex flex-col gap-2 mb-4">
              <Label htmlFor="yearly">
                ປີ <span className="text-red-500">*</span>
              </Label>
              <Select
                value={year ? String(year) : ""}
                onValueChange={(value) => {
                  setYear(Number(value));
                }}
                required
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="ກະລຸນາເລືອກປີ" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    { length: new Date().getFullYear() - 1974 },
                    (_, i) => (
                      <SelectItem
                        key={i}
                        value={(new Date().getFullYear() - i).toString()}
                      >
                        {new Date().getFullYear() - i}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* ------------- Fix Assets Table ------------- */}
            <Table className="overflow-x-auto w-full border table-fixed">
              <TableHeader>
                <TableRow className="[&>*]:border [&>*]:w-[150px] [&>*]:font-bold [&>*]:text-black">
                  <TableHead style={{ width: 200 }}>ຊື່</TableHead>
                  <TableHead>ຈໍານວນ</TableHead>
                  <TableHead>ຫົວໜ່ວຍ</TableHead>
                  <TableHead>ວ.ດ.ປ ຮັບເຂົ້າ</TableHead>
                  <TableHead>ລວມອາຍຸ</TableHead>
                  <TableHead>
                    ມູນຄ່າເດີມ <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead>
                    ຄ່າຫຼຸ້ຍຫ້ຽນສະສົມ <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead>ມູນຄ່າຍັງເຫຼືອ</TableHead>
                  <TableHead>ມູນຄ່າສະສາງ </TableHead>
                  <TableHead>ວັນທີສະສາງ</TableHead>
                  <TableHead style={{ width: 100 }}>ຈັດການ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fixAssets.map((asset, index) => (
                  <TableRow key={index}>
                    {/* Name */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={asset.name}
                        onChange={(e) => {
                          const newAssets = [...fixAssets];
                          newAssets[index].name = e.target.value;
                          setFixAssets(newAssets);
                        }}
                        required
                        readOnly={index < 4}
                      />
                    </TableCell>
                    {/* Count */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(asset.count)}
                        onChange={(e) => {
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "count",
                            data: fixAssets,
                            setData: setFixAssets,
                          });
                        }}
                        className="text-right"
                        // required
                        inputMode="numeric"
                        placeholder="ປ້ອນຈຳນວນ"
                      />
                    </TableCell>
                    {/* Unit */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={asset.unit}
                        onChange={(e) => {
                          const newAssets = [...fixAssets];
                          newAssets[index].unit = e.target.value;
                          setFixAssets(newAssets);
                        }}
                        placeholder="ປ້ອນຫົວໜ່ວຍ"
                      />
                    </TableCell>
                    {/* Received Date */}
                    <TableCell className="border">
                      <DatePicker
                        showLabel={false}
                        value={asset.receivedDate}
                        onChange={(value) => {
                          const newAssets = [...fixAssets];
                          newAssets[index].receivedDate = value;
                          setFixAssets(newAssets);
                        }}
                        // required
                      />
                    </TableCell>
                    {/* Total Age */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={asset.totalAge}
                        onChange={(e) => {
                          const newAssets = [...fixAssets];
                          newAssets[index].totalAge = e.target.value;
                          setFixAssets(newAssets);
                        }}
                        placeholder="1 ປີ, 5 ປີ, ..."
                      />
                    </TableCell>
                    {/* Original Value */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(asset.originalValue)}
                        onChange={(e) => {
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "originalValue",
                            data: fixAssets,
                            setData: setFixAssets,
                          });
                        }}
                        className="text-right"
                        required
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    {/* Depreciation Value */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(asset.depreciationValue)}
                        onChange={(e) => {
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "depreciationValue",
                            data: fixAssets,
                            setData: setFixAssets,
                          });
                        }}
                        className="text-right"
                        required
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    {/* Remaining Value */}
                    <TableCell className="border">
                      {formatNumberWithCommas(
                        parseParenthesisNumber(asset.originalValue) -
                          parseParenthesisNumber(asset.depreciationValue)
                      )}
                    </TableCell>

                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(asset.disposalValue)}
                        onChange={(e) => {
                          // const newAssets = [...fixAssets];
                          // newAssets[index].count = e.target.value;
                          // setFixAssets(newAssets);

                          handleNumericChange({
                            e,
                            index,
                            fieldName: "disposalValue",
                            data: fixAssets,
                            setData: setFixAssets,
                          });
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>

                    <TableCell className="border">
                      <Input
                        type="date"
                        value={asset.disposalDate}
                        onChange={(e) => {
                          const newAssets = [...fixAssets];
                          newAssets[index].disposalDate = e.target.value;
                          setFixAssets(newAssets);
                        }}
                      />
                    </TableCell>
                    {/* Remove Row Button */}
                    <TableCell className="border">
                      {index > 3 ? (
                        <Button
                          onClick={() => removeFixAssetRow(index)}
                          type="button"
                          variant={"ghost"}
                          size={"icon"}
                        >
                          <Trash2 className="text-red-500" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                {/* ------ Add Row Button ------ */}
                <TableRow className="bg-[#F4F4F5]">
                  <TableCell colSpan={11}>
                    <Button
                      type="button"
                      size="icon"
                      onClick={addNewFixAssetRow}
                      className="flex gap-x-2 items-center p-2 my-2 w-auto text-blue-600 border-2 border-blue-600 hover:bg-blue-500 hover:text-white"
                      variant="ghost"
                    >
                      <CirclePlus className="w-4 h-4" />
                      ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow className="bg-gray-100 [&>*]:border">
                  <TableCell className="font-bold text-center">ລວມ</TableCell>
                  <TableCell className="text-right">
                    {computeSumForField(fixAssets, "count")}
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right">
                    {computeSumForField(fixAssets, "originalValue")}
                  </TableCell>
                  <TableCell className="text-right">
                    {/* Sum of Depreciation Value */}

                    {computeSumForField(fixAssets, "depreciationValue")}
                  </TableCell>
                  <TableCell className="text-right">
                    {/* Sum of Remaining Value */}
                    {formatNumberWithCommas(
                      fixAssets.reduce(
                        (acc, asset) =>
                          acc +
                          (parseParenthesisNumber(asset.originalValue) -
                            parseParenthesisNumber(asset.depreciationValue)),
                        0
                      )
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {/* Sum of Disposal Value */}
                    {computeSumForField(fixAssets, "disposalValue")}
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ------------------- File Upload ------------------- */}
            <div className="flex gap-x-1 items-center mt-4">
              <FileUpload
                currentFile={docFile}
                onFileUpload={(file) => {
                  console.log(file, "File Uploaded");
                  setDocFile(file);
                }}
              />
            </div>

            {/* ------------------- Action Buttons ------------------- */}
            <div className="flex gap-x-2 justify-end mt-4">
              <Button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/report/finance",
                    search: { page: 1, startYear: 0, endYear: 0 },
                  })
                }
                variant={"outline"}
              >
                ຍົກເລີກ
              </Button>
              <Button
                disabled={mutation.isPending}
                type="submit"
                className="flex gap-x-2 items-center"
              >
                {mutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    ບັນທຶກ
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}

export default AddFixAssets;
